import { useState, type ReactNode } from 'react'
import { CircleCheck } from 'lucide-react'
import type { TmsCompletenessExample, TmsInvoiceWorklist } from '@numeo/types-main-web-app'
import { Button } from '@/components/ds2'
import type { ThreadLoad } from './cardResults'
import { LOAD_STAGE_LABEL } from './format'
import { ReadyLoadItem } from './ReadyLoadItem'
import { invoiceAllMessage, markDeliveredMessage } from './rowActions'

interface WorklistCardProps {
    worklist: TmsInvoiceWorklist
    loads: ThreadLoad[]
    onSend: (message: string) => void
}

/** Rows a part shows before "Show more": the first few keep all three parts on one screen. */
const PART_PREVIEW = 5

/** "Showing 3 of 12" when the part shows fewer rows than it counts. */
const shownOf = (shown: number, total: number) => (total > shown ? `Showing ${shown} of ${total}` : null)

/** When a delivery was due, in the company's time zone ("Oct 28, 4:00 PM CDT"); the browser's zone when the company's is unknown. */
const dueAt = (iso: string, timeZone: string | undefined) => {
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }
    try {
        return new Date(iso).toLocaleString('en-US', { ...options, timeZone })
    } catch {
        return new Date(iso).toLocaleString('en-US', options)
    }
}

/** What the user asked for from here, in place of the button. */
const Sent = ({ label }: { label: string }) => (
    <span role="status" className="flex items-center gap-1 text-xs text-success">
        <CircleCheck className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {label}
    </span>
)

interface PartProps {
    title: string
    hint: string
    count: number
    /** The part's one action for all its rows ("Invoice all 3"), or nothing. */
    action?: ReactNode
    /** One list item per row the answer holds; the first few are shown until "Show more". */
    items: ReactNode[]
}

/** One step of invoicing: its title and count, what it means, its first rows with "Show more", and how much of it is shown. */
const Part = ({ title, hint, count, action, items }: PartProps) => {
    const [open, setOpen] = useState(false)
    const shown = open ? items : items.slice(0, PART_PREVIEW)
    const hidden = items.length - shown.length
    return (
        <section aria-label={title} className="flex flex-col gap-2">
            <header className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-semibold text-foreground">
                    {title} <span className="font-normal text-foreground-muted">({count})</span>
                </h3>
                <div className="ml-auto">{action}</div>
            </header>
            <p className="text-xs text-foreground-muted">{hint}</p>
            <ul className="flex flex-col divide-y divide-border">{shown}</ul>
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-foreground-muted">
                {shownOf(shown.length, count)}
                {hidden > 0 && (
                    <button type="button" className="font-medium text-accent hover:underline" onClick={() => setOpen(true)}>
                        Show {hidden} more
                    </button>
                )}
            </div>
        </section>
    )
}

/** A load whose delivery window closed while it is not marked delivered: its number, stage, when delivery was due, and the fix. */
const DeliveryPassedItem = ({ load, timeZone, onSend }: { load: TmsCompletenessExample; timeZone?: string; onSend: (message: string) => void }) => {
    const [sent, setSent] = useState(false)
    const number = load.load_number
    return (
        <li aria-label={`Load #${number ?? load.load_id}`} className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2 text-xs first:pt-0 last:pb-0">
            <span className="text-sm font-semibold text-foreground">#{number ?? '—'}</span>
            <span className="text-foreground-muted">{LOAD_STAGE_LABEL[load.stage] ?? load.stage}</span>
            {load.delivery_close_at && <span className="text-foreground-muted">delivery due {dueAt(load.delivery_close_at, timeZone)}</span>}
            {!load.truck_assigned && <span className="text-warning">no truck</span>}
            <div className="ml-auto">
                {sent ? (
                    <Sent label="Asked to mark delivered" />
                ) : (
                    number && (
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                                onSend(markDeliveredMessage([number]))
                                setSent(true)
                            }}
                        >
                            Mark delivered
                        </Button>
                    )
                )}
            </div>
        </li>
    )
}

/** A part's one button for all its rows, shown done once pressed. */
const AllButton = ({ label, done, onClick }: { label: string; done: string; onClick: () => void }) => {
    const [sent, setSent] = useState(false)
    if (sent) return <Sent label={done} />
    return (
        <Button
            size="sm"
            onClick={() => {
                onClick()
                setSent(true)
            }}
        >
            {label}
        </Button>
    )
}

/**
 * The invoicing worklist (`tms_invoices.worklist`), the order invoicing is worked in: the loads ready to invoice, the delivered loads
 * that are not ready yet with the fix each needs, and the loads whose delivery date passed but are not marked delivered. Every row
 * fixes itself in place (invoice, upload or review a document, set a rate, mark delivered), and each part has one button for all.
 */
export const WorklistCard = ({ worklist, loads, onSend }: WorklistCardProps) => {
    const { ready, needs_documents: needs, delivery_passed: passed } = worklist
    const idOf = (number: string | undefined) => loads.find((load) => load.number === number)?.id
    const readyNumbers = ready.rows.map((row) => row.load_numbers[0]).filter((n): n is string => !!n)
    const passedNumbers = (passed?.loads ?? []).map((load) => load.load_number).filter((n): n is string => !!n)
    const nothing = !ready.total && !needs.total && !passed?.total

    if (nothing)
        return <p className="text-sm text-foreground-muted">Nothing waits on invoicing: no load is ready, missing a document or past its delivery date.</p>
    return (
        <div className="flex flex-col gap-5">
            {ready.total > 0 && (
                <Part
                    title="Ready to invoice"
                    hint="These can be invoiced now."
                    count={ready.total}
                    action={
                        readyNumbers.length > 1 && (
                            <AllButton
                                label={`Invoice all ${readyNumbers.length}`}
                                done="Asked to invoice them"
                                onClick={() => onSend(invoiceAllMessage(readyNumbers))}
                            />
                        )
                    }
                    items={ready.rows.map((row, i) => (
                        <ReadyLoadItem key={row.load_id ?? i} row={row} loadId={row.load_id ?? idOf(row.load_numbers[0])} onSend={onSend} />
                    ))}
                />
            )}
            {needs.total > 0 && (
                <Part
                    title="Delivered, needs documents"
                    hint="Fix what each one is missing, then it can be invoiced."
                    count={needs.total}
                    items={needs.rows.map((row, i) => (
                        <ReadyLoadItem key={row.load_id ?? i} row={row} loadId={row.load_id ?? idOf(row.load_numbers[0])} onSend={onSend} />
                    ))}
                />
            )}
            {passed && passed.total > 0 && (
                <Part
                    title="Delivery date passed, not marked delivered"
                    hint="Mark them delivered once you confirm, then they move to invoicing."
                    count={passed.total}
                    action={
                        passedNumbers.length > 1 && (
                            <AllButton
                                label={`Mark all ${passedNumbers.length} delivered`}
                                done="Asked to mark them delivered"
                                onClick={() => onSend(markDeliveredMessage(passedNumbers))}
                            />
                        )
                    }
                    items={passed.loads.map((load) => (
                        <DeliveryPassedItem key={load.load_id} load={load} timeZone={worklist.timezone} onSend={onSend} />
                    ))}
                />
            )}
        </div>
    )
}
